import { useState, useEffect, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import MDBox from "components/MDBox";
import NotificationItem from "examples/Items/NotificationItem";
import MDButton from "components/MDButton";

import { useAuth } from "../../../AuthContext";
import { LanguageContext } from "../../../context/LanguageContext";
import { getUserProfile } from "api/userApi";
import axiosInstance from "api/axiosInstance";
import { COLORS } from "theme/tokens";
import { fmtDate } from "utils/datetime";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  // 2026-05-25 改成 fixed（一律不滑掉 + 跟 sidebar 無縫接）
  const [navbarType, setNavbarType] = useState("fixed");
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null); // ✅ 帳號選單控制
  const [anchorElLang, setAnchorElLang] = useState(null); // 2026-05-25 語系下拉
  const [livePlanCode, setLivePlanCode] = useState(null); // ✅ 即時方案偵測
  const [trialDays, setTrialDays] = useState(null); // 2026-05-25 試用剩餘天數
  // 2026-05-25 站內通知
  const [anchorElNoti, setAnchorElNoti] = useState(null);
  const [notiItems, setNotiItems] = useState([]);
  const [notiUnread, setNotiUnread] = useState(0);
  const [selectedNoti, setSelectedNoti] = useState(null); // 點開看完整內容的那則
  const navigate = useNavigate();
  const location = useLocation(); // ✅ 用於判斷目前頁面路由
  const route = location.pathname.split("/").slice(1);
  const { componentLanguageData, loadLanguageFile, language, changeLanguage } = useContext(LanguageContext);

  useEffect(() => {
    loadLanguageFile("DashboardNavbar", "Navbars/DashboardNavbar/DashboardNavbar_lang");
  }, [language, loadLanguageFile]);

  const LANGUAGE = useMemo(() => {
    return componentLanguageData.DashboardNavbar || { Main: {} };
  }, [componentLanguageData]);

   // ✅ 判斷是否為策略摘要頁（教學按鈕用）
  const isStrategySummaryPage = location.pathname.includes("/strategy-summary");

  useEffect(() => {
    // 2026-05-25 一律 fixed：依 Sean 要求 top bar 滑動時也維持在最上面 + 跟 sidebar 無縫接
    setNavbarType("fixed");

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleCloseMenu = () => setOpenMenu(false);

  // ✅ 帳號選單控制
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const { logout, isAuthenticated, user } = useAuth();

  // ✅ 即時同步使用者方案資訊，解決 localStorage 過期問題
  useEffect(() => {
    if (isAuthenticated) {
      getUserProfile()
        .then((res) => {
          const fetchedPlanCode = res.data?.user?.plan?.code || res.data?.user?.planCode;
          if (fetchedPlanCode) {
            setLivePlanCode(fetchedPlanCode.toUpperCase());
          }
          // 2026-05-25 同步試用剩餘天數（TRIAL pill 顯示用）
          const days = res.data?.user?.remainingDays;
          if (days !== undefined && days !== null) {
            setTrialDays(days);
          }
        })
        .catch((err) => console.error("❌ 導覽列即時同步方案失敗:", err));
    }
  }, [isAuthenticated]);
  // 2026-05-25 通知：抓清單 + 未讀數（mount + 每 3 分鐘輪詢）
  const fetchNotifications = () => {
    axiosInstance
      .get("/users/notifications")
      .then((res) => {
        setNotiItems(res.data?.items || []);
        setNotiUnread(res.data?.unreadCount || 0);
      })
      .catch(() => {});
  };
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNotifications();
    const t = setInterval(fetchNotifications, 3 * 60 * 1000);
    return () => clearInterval(t);
  }, [isAuthenticated, language]); // 2026-06-14：language 進 deps → 切 App 語言即時重抓（後端依 accept-Language 回對應語系）

  // 打開鈴鐺：標記已讀（紅點消失），但清單仍顯示
  const handleOpenNoti = (e) => {
    setAnchorElNoti(e.currentTarget);
    if (notiUnread > 0) {
      axiosInstance
        .post("/users/notifications/read")
        .then(() => {
          setNotiUnread(0);
          setNotiItems((prev) => prev.map((n) => ({ ...n, isUnread: false })));
        })
        .catch(() => {});
    }
  };
  const handleCloseNoti = () => setAnchorElNoti(null);

  const handleLogout = () => {
    logout();
    handleCloseUserMenu();
    navigate("/authentication/sign-in");
  };
  const handleGoProfile = () => {
    handleCloseUserMenu();
    navigate("/profile/edit");
  };

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode, miniSidenav })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })} display="flex" alignItems="center">
          <IconButton 
            size="small" 
            disableRipple 
            color="inherit" 
            sx={(theme) => ({
              ...navbarMobileMenu(theme),
              border: "1px solid rgba(0,0,0,0.2)",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            })} 
            onClick={handleMiniSidenav}
          >
            <img src="/images/menu.png" alt="menu" style={{ width: "22px", height: "22px" }} />
          </IconButton>
          <MDBox ml={2} mr={2}>
            {(() => {
              // 2026-05-25 依 Sean 要求：FREE/STARTER/PRO 用 Member Level 圖片
              // 只有 TRIAL 例外用黑底 pill 顯示「N 天試用中」(動態天數)
              const currentPlanCode = livePlanCode || (user?.plan?.code || user?.planCode || "FREE").toUpperCase();
              const isTrial = currentPlanCode === "STARTER" || currentPlanCode === "FREE_TRIAL";

              if (isTrial) {
                const days = trialDays !== null ? trialDays : 15;
                return (
                  <MDBox
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 2,
                      height: 32,
                      borderRadius: "8px",
                      background: "#1f2937",
                      color: COLORS.white,
                      fontSize: "13px",
                      fontWeight: 600,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`${days} ${LANGUAGE.Main.PlanTrial || "天試用中"}`}
                  </MDBox>
                );
              }

              // 非 trial → 顯示方案名稱文字 tag（簡潔名：去掉（說明）括號）
              const planNameMap = {
                FREE: LANGUAGE.Main.PlanFree,
                GROWTH: LANGUAGE.Main.PlanStarter,
                PRO: LANGUAGE.Main.PlanPro,
                // 舊方案向後相容
                BRONZE: LANGUAGE.Main.PlanStarter,
                SILVER: LANGUAGE.Main.PlanPro,
                GOLD: LANGUAGE.Main.PlanPro,
              };
              const rawPlanName = planNameMap[currentPlanCode] || currentPlanCode;
              const shortPlanName = (rawPlanName || "")
                .replace(/（[^）]*）/g, "")
                .replace(/\([^)]*\)/g, "")
                .trim();
              const planTagBg = {
                FREE: COLORS.grayDark, GROWTH: COLORS.main, PRO: COLORS.purple,
                BRONZE: COLORS.main, SILVER: COLORS.grayDark, GOLD: COLORS.purple,
              }[currentPlanCode] || COLORS.purple;
              return (
                <MDBox
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 2,
                    height: 32,
                    borderRadius: "8px",
                    background: planTagBg,
                    color: COLORS.white,
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortPlanName}
                </MDBox>
              );
            })()}
          </MDBox>
        </MDBox>


        {!isMini && (

          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center" gap={1}>
              {/* ✅ 僅在策略摘要頁顯示「操作教學」按鈕 (2026-05-25 文字縮短) */}
              {isStrategySummaryPage && (
                <MDButton
                  className="help-tour-btn"
                  variant="outlined"
                  color="info"
                  size="small"
                  sx={{ mr: 0.5 }}
                  onClick={() => window.dispatchEvent(new Event("restartTour"))}
                >
                  {LANGUAGE.Main.Content4}
                </MDButton>
              )}

              {/* 2026-05-25 語系下拉（tailadmin compact 樣式：button + chevron + Menu） */}
              {(() => {
                const langLabelMap = {
                  "zh-TW": LANGUAGE.Main.LangShort || "繁中",
                  "zh-CN": "简中",
                  "en-US": "EN US",
                  "en-GB": "EN UK",
                };
                const currentLangLabel = langLabelMap[language] || "繁中";
                return (
                  <>
                    <MDBox
                      onClick={(e) => setAnchorElLang(e.currentTarget)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        height: 36,
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: COLORS.black,
                        background: COLORS.white,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: COLORS.grayMedian,
                          background: COLORS.grayLightest,
                        },
                      }}
                    >
                      <span style={{ lineHeight: 1 }}>{currentLangLabel}</span>
                      <KeyboardArrowDownIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
                    </MDBox>
                    <Menu
                      anchorEl={anchorElLang}
                      open={Boolean(anchorElLang)}
                      onClose={() => setAnchorElLang(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          minWidth: 160,
                          borderRadius: "10px",
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)",
                          "& .MuiMenuItem-root": {
                            fontSize: "13px",
                            py: 1,
                            px: 1.5,
                            "&.Mui-selected": {
                              background: COLORS.lighter,
                              color: COLORS.main,
                              fontWeight: 600,
                            },
                            "&:hover": { background: COLORS.grayLightest },
                          },
                        },
                      }}
                    >
                      {[
                        { code: "zh-TW", label: "繁體中文" },
                        { code: "zh-CN", label: "简体中文" },
                        { code: "en-US", label: "English (US)" },
                        { code: "en-GB", label: "English (UK)" },
                      ].map((opt) => (
                        <MenuItem
                          key={opt.code}
                          selected={language === opt.code}
                          onClick={() => {
                            changeLanguage(opt.code);
                            setAnchorElLang(null);
                          }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                );
              })()}

              {/* 2026-05-25 通知 bell (Bell.png + 圓形邊框 + 下拉清單) */}
              {isAuthenticated && (
                <>
                  <Tooltip title={LANGUAGE.Main.Notifications || "通知"}>
                    <IconButton
                      onClick={handleOpenNoti}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: "1px solid #e5e7eb",
                        p: 0,
                        "&:hover": { background: COLORS.grayLightest, borderColor: COLORS.grayMedian },
                      }}
                    >
                      <Badge
                        color="error"
                        variant="dot"
                        overlap="circular"
                        invisible={notiUnread === 0}
                      >
                        <img src="/images/menu/Bell.png" alt="notifications" style={{ width: 20, height: 20 }} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorElNoti}
                    open={Boolean(anchorElNoti)}
                    onClose={handleCloseNoti}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        width: 360,
                        maxHeight: 440,
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)",
                        overflow: "hidden",
                      },
                    }}
                  >
                    {/* header */}
                    <MDBox
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #eef0f3",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <MDBox sx={{ width: 3, height: 16, background: COLORS.main, borderRadius: 1 }} />
                      <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.black }}>
                        {LANGUAGE.Main.Notifications || "通知"}
                      </span>
                    </MDBox>
                    {/* list */}
                    {notiItems.length === 0 ? (
                      <MDBox sx={{ px: 2, py: 4, textAlign: "center", color: COLORS.grayDark, fontSize: 13 }}>
                        {LANGUAGE.Main.NotiEmpty || "目前沒有通知"}
                      </MDBox>
                    ) : (
                      notiItems.map((n) => {
                        const typeColor =
                          { INFO: "#2d80eb", WARNING: COLORS.warning, PROMO: COLORS.main, MAINTENANCE: COLORS.error }[n.type] ||
                          COLORS.grayMedian;
                        return (
                          <MDBox
                            key={n.id}
                            onClick={() => {
                              setSelectedNoti(n);
                              handleCloseNoti();
                            }}
                            sx={{
                              px: 2,
                              py: 1.5,
                              borderBottom: "1px solid #f5f5f5",
                              display: "flex",
                              gap: 1.2,
                              alignItems: "flex-start",
                              cursor: "pointer",
                              background: n.isUnread ? "#fff8f2" : COLORS.white,
                              transition: "background 0.15s",
                              "&:hover": { background: COLORS.grayLightest },
                            }}
                          >
                            <MDBox
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: typeColor,
                                mt: "5px",
                                flexShrink: 0,
                              }}
                            />
                            <MDBox sx={{ minWidth: 0, flex: 1 }}>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: COLORS.black,
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {n.title}
                              </span>
                              {/* 2026-05-25 內文摘要：截斷 2 行，完整內容點開看 */}
                              <MDBox
                                sx={{
                                  fontSize: 12,
                                  color: COLORS.grayDark,
                                  mt: 0.3,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  wordBreak: "break-word",
                                }}
                              >
                                {n.content}
                              </MDBox>
                              <MDBox
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mt: 0.5,
                                }}
                              >
                                <span style={{ fontSize: 11, color: COLORS.grayMedian }}>
                                  {n.publishedAt ? fmtDate(n.publishedAt) : ""}
                                </span>
                                <span style={{ fontSize: 11, color: COLORS.main, fontWeight: 500 }}>
                                  {LANGUAGE.Main.NotiViewMore || "查看詳情 ›"}
                                </span>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        );
                      })
                    )}
                  </Menu>

                  {/* 2026-05-25 通知完整內容 Dialog */}
                  <Dialog
                    open={Boolean(selectedNoti)}
                    onClose={() => setSelectedNoti(null)}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ sx: { borderRadius: 3 } }}
                  >
                    {selectedNoti && (
                      <>
                        {/* header */}
                        <MDBox
                          sx={{
                            px: 3,
                            py: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            borderBottom: "1px solid #eef0f3",
                          }}
                        >
                          <MDBox sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                            <MDBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                flexShrink: 0,
                                background:
                                  {
                                    INFO: "#2d80eb",
                                    WARNING: COLORS.warning,
                                    PROMO: COLORS.main,
                                    MAINTENANCE: COLORS.error,
                                  }[selectedNoti.type] || COLORS.grayMedian,
                              }}
                            />
                            <span style={{ fontSize: 18, fontWeight: 700, color: COLORS.black }}>
                              {selectedNoti.title}
                            </span>
                          </MDBox>
                          <IconButton size="small" onClick={() => setSelectedNoti(null)} sx={{ ml: 1 }}>
                            <Icon fontSize="small">close</Icon>
                          </IconButton>
                        </MDBox>
                        <DialogContent sx={{ px: 3, py: 2.5 }}>
                          <span style={{ fontSize: 12, color: COLORS.grayMedian, display: "block", marginBottom: 12 }}>
                            {selectedNoti.publishedAt ? fmtDate(selectedNoti.publishedAt) : ""}
                          </span>
                          <MDBox
                            sx={{
                              fontSize: 14,
                              color: COLORS.black,
                              lineHeight: 1.8,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {selectedNoti.content}
                          </MDBox>
                        </DialogContent>
                      </>
                    )}
                  </Dialog>
                </>
              )}

              {/* ✅ 只在已登入時顯示帳號選單 (2026-05-25 圓形邊框 + Frame 81.png) */}
              {isAuthenticated && (
                <>
                  <Tooltip title={LANGUAGE.Main.Content1}>
                    <IconButton
                      onClick={handleOpenUserMenu}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: "1px solid #e5e7eb",
                        p: 0,
                        "&:hover": { background: COLORS.grayLightest, borderColor: COLORS.grayMedian },
                      }}
                    >
                      <img src="/images/menu/Frame%2081.png" alt="user" style={{ width: 20, height: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: "8px" }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    keepMounted
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    PaperProps={{
                      sx: {
                        minWidth: 180,
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)",
                        "& .MuiMenuItem-root": {
                          fontSize: "13px",
                          py: 1,
                          "&:hover": { background: COLORS.grayLightest },
                        },
                      },
                    }}
                  >
                    <MenuItem onClick={handleGoProfile}>
                      <Icon fontSize="small" sx={{ mr: 1, color: COLORS.grayDark }}>account_circle</Icon>
                      {LANGUAGE.Main.Content2}
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <Icon fontSize="small" sx={{ mr: 1, color: COLORS.grayDark }}>logout</Icon>
                      {LANGUAGE.Main.Content3}
                    </MenuItem>
                  </Menu>
                </>
              )}

              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
